import { useReducer, useCallback, useRef, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, FolderUp, X, Check, AlertCircle, RotateCw, File as FileIcon, Video, Music, FileText, Folder } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useNotification } from '@/hooks/useNotification';
import { addResource, addResourceWithSnapshot } from '../../../apiServices/resourceApi';
import {
  putToStorageResource,
  putToStorageVideoSnapShot,
  putToStorageDefault,
} from '../../../apiServices/storageApiService';
import { addProject } from '../../../apiServices/projectApi';
import { pdfToPng } from '../setPictureModal/SetPictureModalUtilFunctions';
import { getLowerCaseFileExtensionInFile, getNameFromFileName, getExtFromFileName } from '../../../util/UtilFunctions';
import { validateLocalVideoFileRendability } from '../../../util/FileTypeSupportCheck';

// ── Types ────────────────────────────────────────────────────────────

interface FileEntry {
  id: string;
  file: File;
  status: 'pending' | 'generating' | 'uploading' | 'done' | 'failed';
  progress: number;
  thumbnail?: string;
  pdfThumbnailFile?: File;
  videoThumbnailBlob?: Blob;
  parentFolder?: string;       // folder entry ID this file belongs to
}

interface FolderEntry {
  id: string;
  name: string;
  status: 'pending' | 'uploading' | 'done' | 'failed';
  progress: number;
  apiId?: string;              // API-assigned folder ID after creation
  childIds: string[];
}

interface UploadState {
  files: FileEntry[];
  folders: FolderEntry[];
}

type Action =
  | { type: 'ADD_FILES'; files: FileEntry[] }
  | { type: 'ADD_FOLDER'; folder: FolderEntry; children: FileEntry[] }
  | { type: 'REMOVE_ENTRY'; id: string }
  | { type: 'UPDATE_FILE'; id: string; patch: Partial<FileEntry> }
  | { type: 'UPDATE_FOLDER'; id: string; patch: Partial<FolderEntry> }
  | { type: 'RETRY_FAILED' };

interface FileUploadingModalProps {
  closeModal: () => void;
  onRefresh?: () => void;
  parentId: string;
  existingNames?: string[];
  useCase: 'folder' | 'project';
}

// ── Reducer ──────────────────────────────────────────────────────────

function reducer(state: UploadState, action: Action): UploadState {
  switch (action.type) {
    case 'ADD_FILES':
      return { ...state, files: [...state.files, ...action.files] };
    case 'ADD_FOLDER':
      return {
        files: [...state.files, ...action.children],
        folders: [...state.folders, action.folder],
      };
    case 'REMOVE_ENTRY': {
      // Remove a file, or a folder + its children
      const folder = state.folders.find((f) => f.id === action.id);
      if (folder) {
        const childSet = new Set(folder.childIds);
        return {
          files: state.files.filter((f) => !childSet.has(f.id)),
          folders: state.folders.filter((f) => f.id !== action.id),
        };
      }
      return { ...state, files: state.files.filter((f) => f.id !== action.id) };
    }
    case 'UPDATE_FILE':
      return {
        ...state,
        files: state.files.map((f) => (f.id === action.id ? { ...f, ...action.patch } : f)),
      };
    case 'UPDATE_FOLDER':
      return {
        ...state,
        folders: state.folders.map((f) => (f.id === action.id ? { ...f, ...action.patch } : f)),
      };
    case 'RETRY_FAILED':
      return {
        files: state.files.map((f) => (f.status === 'failed' ? { ...f, status: 'pending' as const, progress: 0 } : f)),
        folders: state.folders.map((f) => (f.status === 'failed' ? { ...f, status: 'pending' as const, progress: 0 } : f)),
      };
    default:
      return state;
  }
}

const INITIAL_STATE: UploadState = { files: [], folders: [] };

// ── Helpers ──────────────────────────────────────────────────────────

let idSeq = 0;
const nextId = () => `upload-${++idSeq}-${Date.now()}`;

function deduplicateName(name: string, existing: string[]): string {
  if (!existing.includes(name)) return name;
  const base = getNameFromFileName(name);
  const ext = getExtFromFileName(name);
  let n = 1;
  let candidate = `${base}_COPY.${ext}`;
  while (existing.includes(candidate)) {
    n++;
    candidate = `${base}_COPY_${n}.${ext}`;
  }
  return candidate;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function captureVideoThumbnail(file: File): Promise<{ url: string; blob: Blob } | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    const src = URL.createObjectURL(file);
    video.src = src;

    const cleanup = () => URL.revokeObjectURL(src);
    const fail = () => { cleanup(); resolve(null); };

    video.onerror = fail;
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration / 2);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          cleanup();
          blob ? resolve({ url: URL.createObjectURL(blob), blob }) : resolve(null);
        }, 'image/jpeg', 0.8);
      } catch {
        fail();
      }
    };
    // Timeout: if seeking takes too long, resolve without thumbnail
    setTimeout(() => { if (video.readyState < 2) fail(); }, 8000);
  });
}

async function capturePdfThumbnail(file: File): Promise<{ url: string; pdfFile: File } | null> {
  try {
    const pngFile = await pdfToPng(file);
    return { url: URL.createObjectURL(pngFile), pdfFile: pngFile };
  } catch {
    return null;
  }
}

function getMaxDepth(files: File[]): number {
  return files.reduce((max, f) => Math.max(max, f.webkitRelativePath.split('/').length), 0);
}

// ── FileListItem ─────────────────────────────────────────────────────

function FileListItem({
  entry,
  onRemove,
  dispatch,
}: {
  entry: FileEntry;
  onRemove: () => void;
  dispatch: React.Dispatch<Action>;
}) {
  // Generate thumbnail on mount
  useEffect(() => {
    if (entry.thumbnail || entry.status !== 'pending') return;
    const mime = entry.file.type;

    if (mime.startsWith('image/')) {
      const url = URL.createObjectURL(entry.file);
      dispatch({ type: 'UPDATE_FILE', id: entry.id, patch: { thumbnail: url } });
      return;
    }

    if (mime.startsWith('video/') && validateLocalVideoFileRendability(entry.file)) {
      dispatch({ type: 'UPDATE_FILE', id: entry.id, patch: { status: 'generating' } });
      captureVideoThumbnail(entry.file).then((result) => {
        dispatch({
          type: 'UPDATE_FILE',
          id: entry.id,
          patch: {
            status: 'pending',
            ...(result ? { thumbnail: result.url, videoThumbnailBlob: result.blob } : {}),
          },
        });
      });
      return;
    }

    if (mime === 'application/pdf') {
      dispatch({ type: 'UPDATE_FILE', id: entry.id, patch: { status: 'generating' } });
      capturePdfThumbnail(entry.file).then((result) => {
        dispatch({
          type: 'UPDATE_FILE',
          id: entry.id,
          patch: {
            status: 'pending',
            ...(result ? { thumbnail: result.url, pdfThumbnailFile: result.pdfFile } : {}),
          },
        });
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (entry.thumbnail) URL.revokeObjectURL(entry.thumbnail);
    };
  }, [entry.thumbnail]);

  const mime = entry.file.type;
  const icon = mime.startsWith('video/') ? <Video className="h-5 w-5 text-blue-500" />
    : mime.startsWith('audio/') ? <Music className="h-5 w-5 text-purple-500" />
    : mime.startsWith('image/') ? <FileIcon className="h-5 w-5 text-emerald-500" />
    : mime === 'application/pdf' ? <FileText className="h-5 w-5 text-red-500" />
    : <FileIcon className="h-5 w-5 text-muted-foreground" />;

  const preview = entry.thumbnail
    ? <img src={entry.thumbnail} alt="" className="h-10 w-10 rounded object-cover" />
    : entry.status === 'generating'
      ? <div className="flex h-10 w-10 items-center justify-center rounded bg-secondary"><Spinner size="sm" /></div>
      : <div className="flex h-10 w-10 items-center justify-center rounded bg-secondary">{icon}</div>;

  const statusBadge = entry.status === 'done' ? <Check className="h-4 w-4 text-primary" />
    : entry.status === 'failed' ? <AlertCircle className="h-4 w-4 text-destructive" />
    : entry.status === 'uploading' ? <Spinner size="sm" />
    : null;

  const canRemove = entry.status === 'pending' || entry.status === 'failed';

  return (
    <li className={`flex items-center gap-3 rounded-md px-2 py-1.5 ${entry.status === 'failed' ? 'bg-destructive-light' : ''}`}>
      {preview}
      <div className="relative min-w-0 flex-1">
        {entry.status === 'uploading' && (
          <div className="absolute inset-0 rounded bg-primary-light/50" style={{ width: `${entry.progress}%` }} />
        )}
        <p className="relative truncate text-sm font-medium text-accent-foreground">{entry.file.name}</p>
        <p className="relative text-xs text-muted-foreground">
          {entry.status === 'uploading' ? `${entry.progress}%` : entry.status === 'generating' ? 'Generating preview…' : formatBytes(entry.file.size)}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {statusBadge}
        {canRemove && (
          <button onClick={onRemove} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

function FolderListItem({
  folder,
  childEntries,
  onRemove,
}: {
  folder: FolderEntry;
  childEntries: FileEntry[];
  onRemove: () => void;
}) {
  const totalSize = childEntries.reduce((sum, f) => sum + f.file.size, 0);
  const doneChildren = childEntries.filter((f) => f.status === 'done').length;

  const statusBadge = folder.status === 'done' ? <Check className="h-4 w-4 text-primary" />
    : folder.status === 'failed' ? <AlertCircle className="h-4 w-4 text-destructive" />
    : folder.status === 'uploading' ? <Spinner size="sm" />
    : null;

  const canRemove = folder.status === 'pending' || folder.status === 'failed';

  return (
    <li className={`flex items-center gap-3 rounded-md px-2 py-1.5 ${folder.status === 'failed' ? 'bg-destructive-light' : ''}`}>
      <div className="flex h-10 w-10 items-center justify-center rounded bg-amber-50">
        <Folder className="h-5 w-5 text-amber-500" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-accent-foreground">{folder.name}</p>
        <p className="text-xs text-muted-foreground">
          {folder.status === 'uploading'
            ? `${doneChildren}/${childEntries.length} files`
            : `${childEntries.length} files · ${formatBytes(totalSize)}`}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {statusBadge}
        {canRemove && (
          <button onClick={onRemove} className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-muted-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </li>
  );
}

// ── Main Component ───────────────────────────────────────────────────

export default function FileUploadingModal({
  closeModal,
  onRefresh,
  parentId,
  existingNames = [],
  useCase,
}: FileUploadingModalProps) {
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [uploading, setUploading] = useState(false);

  // Ref to always read fresh state in async upload callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Track all names (existing + queued) for deduplication
  const allNames = useRef(new Set(existingNames));
  useEffect(() => {
    allNames.current = new Set([
      ...existingNames,
      ...state.files.map((f) => f.file.name),
    ]);
  }, [existingNames, state.files]);

  // ── Add files ──────────────────────────────────────────────────────

  const addFiles = useCallback((rawFiles: FileList) => {
    const fileArray = [...rawFiles];
    const hasPaths = fileArray.some((f) => f.webkitRelativePath !== '');

    if (hasPaths) {
      const depth = getMaxDepth(fileArray);
      if (depth > 2) {
        notify({ type: 'info', message: 'Folder structure is too deep (max 2 levels)' });
        return;
      }

      const folderName = fileArray[0].webkitRelativePath.split('/')[0];
      const folderId = nextId();
      const children: FileEntry[] = fileArray.map((f) => ({
        id: nextId(),
        file: getLowerCaseFileExtensionInFile(f),
        status: 'pending' as const,
        progress: 0,
        parentFolder: folderId,
      }));

      const folder: FolderEntry = {
        id: folderId,
        name: folderName,
        status: 'pending',
        progress: 0,
        childIds: children.map((c) => c.id),
      };

      dispatch({ type: 'ADD_FOLDER', folder, children });
    } else {
      const entries: FileEntry[] = fileArray.map((raw) => {
        const file = getLowerCaseFileExtensionInFile(raw);
        const name = deduplicateName(file.name, [...allNames.current]);
        allNames.current.add(name);
        const renamed = name !== file.name ? new File([file], name, { type: file.type }) : file;
        return { id: nextId(), file: renamed, status: 'pending' as const, progress: 0 };
      });

      // Filter already-done files
      const doneNames = new Set(stateRef.current.files.filter((f) => f.status === 'done').map((f) => f.file.name));
      const filtered = entries.filter((f) => !doneNames.has(f.file.name));
      if (filtered.length < entries.length) {
        notify({ type: 'info', message: 'Some files were already uploaded' });
      }
      if (filtered.length > 0) {
        dispatch({ type: 'ADD_FILES', files: filtered });
      }
    }
  }, [notify]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  }, [addFiles]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ── Upload logic ───────────────────────────────────────────────────

  const uploadSingleFile = useCallback(async (fileId: string, parent: string) => {
    // Read fresh state
    const entry = stateRef.current.files.find((f) => f.id === fileId);
    if (!entry || (entry.status !== 'pending' && entry.status !== 'generating')) return;

    // Wait for thumbnail generation to finish
    while (stateRef.current.files.find((f) => f.id === fileId)?.status === 'generating') {
      await new Promise((r) => setTimeout(r, 200));
    }
    // Re-read after waiting
    const freshEntry = stateRef.current.files.find((f) => f.id === fileId)!;

    dispatch({ type: 'UPDATE_FILE', id: fileId, patch: { status: 'uploading', progress: 0 } });

    try {
      const progressCb = (progress: { loaded: number; total: number }) => {
        dispatch({ type: 'UPDATE_FILE', id: fileId, patch: { progress: Math.round((progress.loaded / progress.total) * 100) } });
      };
      const storageResponse = await putToStorageResource(freshEntry.file, progressCb);

      // Video with thumbnail
      if (freshEntry.file.type.startsWith('video/') && freshEntry.videoThumbnailBlob) {
        const thumbResponse = await putToStorageVideoSnapShot(freshEntry.videoThumbnailBlob);
        await addResourceWithSnapshot(storageResponse.key, parent, freshEntry.file.name, thumbResponse.key);
      }
      // PDF with thumbnail
      else if (freshEntry.file.type === 'application/pdf' && freshEntry.pdfThumbnailFile) {
        const thumbResponse = await putToStorageDefault(freshEntry.pdfThumbnailFile, freshEntry.pdfThumbnailFile.name);
        await addResourceWithSnapshot(storageResponse.key, parent, freshEntry.file.name, thumbResponse.key);
      }
      // Regular file
      else {
        await addResource(storageResponse.key, parent, freshEntry.file.name);
      }

      dispatch({ type: 'UPDATE_FILE', id: fileId, patch: { status: 'done', progress: 100 } });
    } catch (err) {
      console.error('Upload failed:', freshEntry.file.name, err);
      dispatch({ type: 'UPDATE_FILE', id: fileId, patch: { status: 'failed' } });
      throw err;
    }
  }, []);

  const uploadFolder = useCallback(async (folderId: string, parent: string) => {
    const folder = stateRef.current.folders.find((f) => f.id === folderId);
    if (!folder) return;

    dispatch({ type: 'UPDATE_FOLDER', id: folderId, patch: { status: 'uploading' } });

    try {
      const response = await addProject({ title: folder.name, type: 'folder', parent });
      const apiId = response.response;
      dispatch({ type: 'UPDATE_FOLDER', id: folderId, patch: { apiId } });

      // Upload children — all in parallel
      const results = await Promise.allSettled(
        folder.childIds.map((childId) => uploadSingleFile(childId, apiId)),
      );

      const allOk = results.every((r) => r.status === 'fulfilled');
      dispatch({ type: 'UPDATE_FOLDER', id: folderId, patch: { status: allOk ? 'done' : 'failed', progress: 100 } });
      if (!allOk) throw new Error('Some folder files failed');
    } catch (err) {
      dispatch({ type: 'UPDATE_FOLDER', id: folderId, patch: { status: 'failed' } });
      throw err;
    }
  }, [uploadSingleFile]);

  const handleUpload = useCallback(async () => {
    const pendingFiles = stateRef.current.files.filter((f) => f.status === 'pending' && !f.parentFolder);
    const pendingFolders = stateRef.current.folders.filter((f) => f.status === 'pending');

    if (pendingFiles.length === 0 && pendingFolders.length === 0) {
      notify({ type: 'info', message: 'Nothing to upload' });
      return;
    }

    setUploading(true);

    const results = await Promise.allSettled([
      ...pendingFiles.map((f) => uploadSingleFile(f.id, parentId)),
      ...pendingFolders.map((f) => uploadFolder(f.id, parentId)),
    ]);

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;
    setUploading(false);

    if (succeeded > 0) {
      notify({
        type: failed > 0 ? 'info' : 'success',
        message: failed > 0
          ? `${succeeded} uploaded, ${failed} failed`
          : succeeded === 1 ? 'File uploaded' : 'Files uploaded',
      });
      if (useCase === 'project') {
        navigate(`/in/project/${parentId}/allfiles`);
      }
      onRefresh?.();
      closeModal();
    } else {
      notify({ type: 'error', message: 'Upload failed' });
    }
  }, [parentId, useCase, navigate, onRefresh, closeModal, notify, uploadSingleFile, uploadFolder]);

  const handleRetry = useCallback(() => {
    dispatch({ type: 'RETRY_FAILED' });
  }, []);

  // ── Derived state ──────────────────────────────────────────────────

  const { files, folders } = state;
  const topLevelFiles = files.filter((f) => !f.parentFolder);
  const isGenerating = files.some((f) => f.status === 'generating');
  const hasPending = files.some((f) => f.status === 'pending' && !f.parentFolder) || folders.some((f) => f.status === 'pending');
  const doneCount = topLevelFiles.filter((f) => f.status === 'done').length + folders.filter((f) => f.status === 'done').length;
  const failedCount = topLevelFiles.filter((f) => f.status === 'failed').length + folders.filter((f) => f.status === 'failed').length;
  const totalCount = topLevelFiles.length + folders.length;
  const hasEntries = totalCount > 0;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      {/* Dropzone */}
      <div
        className="flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-6 transition hover:border-primary"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <label className="flex cursor-pointer flex-col items-center gap-2 text-muted-foreground hover:text-primary">
          <Upload className="h-8 w-8" />
          <span className="text-sm font-medium">Drag or select files</span>
          <input type="file" className="hidden" multiple onChange={handleFileSelect} />
        </label>

        {useCase !== 'project' && (
          <label className="flex cursor-pointer items-center gap-1.5 text-sm font-medium text-primary hover:text-primary-hover">
            <FolderUp className="h-4 w-4" />
            Upload folder
            <input
              type="file"
              className="hidden"
              multiple
              // @ts-expect-error webkitdirectory is valid but missing from React types
              webkitdirectory=""
              // @ts-expect-error directory is valid but missing from React types
              directory=""
              onChange={handleFileSelect}
            />
          </label>
        )}
      </div>

      {/* File list */}
      {hasEntries && (
        <ul className="max-h-60 space-y-1 overflow-y-auto">
          {folders.map((folder) => {
            const childEntries = files.filter((f) => f.parentFolder === folder.id);
            return (
              <FolderListItem
                key={folder.id}
                folder={folder}
                childEntries={childEntries}
                onRemove={() => dispatch({ type: 'REMOVE_ENTRY', id: folder.id })}
              />
            );
          })}
          {/* Show folder children indented */}
          {folders.map((folder) =>
            files
              .filter((f) => f.parentFolder === folder.id)
              .map((entry) => (
                <div key={entry.id} className="pl-6">
                  <FileListItem
                    entry={entry}
                    onRemove={() => dispatch({ type: 'REMOVE_ENTRY', id: entry.id })}
                    dispatch={dispatch}
                  />
                </div>
              )),
          )}
          {topLevelFiles.map((entry) => (
            <FileListItem
              key={entry.id}
              entry={entry}
              onRemove={() => dispatch({ type: 'REMOVE_ENTRY', id: entry.id })}
              dispatch={dispatch}
            />
          ))}
        </ul>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {totalCount > 1 && (
            <span>
              {doneCount}/{totalCount} uploaded
              {failedCount > 0 && <span className="ml-1 text-destructive">({failedCount} failed)</span>}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {failedCount > 0 && !uploading && (
            <Button variant="secondary" size="sm" onClick={handleRetry}>
              <RotateCw className="mr-1 h-3.5 w-3.5" />
              Retry failed
            </Button>
          )}
          <Button
            onClick={handleUpload}
            disabled={!hasPending || uploading || isGenerating}
          >
            {uploading ? (
              <><Spinner size="sm" /> Uploading…</>
            ) : isGenerating ? (
              <><Spinner size="sm" /> Generating previews…</>
            ) : (
              'Upload'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
